import { Link, useSearchParams } from "react-router";
import type { VenueApprovalStatus } from "../../../lib/api";
import { consoleApi } from "../../../lib/admin-console-api";
import { useAppPreferences } from "../../../lib/preferences";
import { LoadState, PageHeader, StatusChip, useFormatters, useLoad } from "../ui";

const STATUS_FILTERS: Array<{ id: "" | VenueApprovalStatus; label: string }> = [
  { id: "", label: "All" },
  { id: "approved", label: "Live" },
  { id: "pending", label: "Pending" },
  { id: "removal_requested", label: "Removal requested" },
  { id: "rejected", label: "Rejected" },
  { id: "removed", label: "Removed" },
];

export function VenuesPage() {
  const { t } = useAppPreferences();
  const { dateOnly } = useFormatters();
  const [params, setParams] = useSearchParams();
  const status = (params.get("status") ?? "") as "" | VenueApprovalStatus;
  const query = params.get("q") ?? "";
  const missingCommission = params.get("commission") === "missing";
  const venues = useLoad(() => consoleApi.listVenues().then((response) => response.venues), []);
  const needle = query.trim().toLowerCase();
  const visible = (venues.data ?? []).filter(
    (venue) =>
      (!status || venue.approval_status === status) &&
      (!missingCommission || (!venue.has_commission_rules && ["approved", "removal_requested"].includes(venue.approval_status))) &&
      (!needle || `${venue.name} ${venue.city} ${venue.provider_name}`.toLowerCase().includes(needle)),
  );
  const update = (next: Record<string, string>) => {
    const merged = { status, q: query, commission: missingCommission ? "missing" : "", ...next };
    setParams(Object.fromEntries(Object.entries(merged).filter(([, value]) => value)));
  };

  return (
    <div className="console-page">
      <PageHeader description={t("Every physical venue, whether it's live, and its commission terms.")} kicker={t("Records")} title={t("Venues")} />
      <div className="console-toolbar">
        <input
          aria-label={t("Filter venues")}
          className="ops-input console-filter-input"
          onChange={(event) => update({ q: event.target.value })}
          placeholder={t("Filter by venue, city or provider")}
          value={query}
        />
        <div className="console-pills">
          {STATUS_FILTERS.map((item) => (
            <button aria-pressed={status === item.id} className="ops-filter-pill" data-active={status === item.id} key={item.id || "all"} onClick={() => update({ status: item.id })} type="button">
              {t(item.label)}
            </button>
          ))}
          <button aria-pressed={missingCommission} className="ops-filter-pill" data-active={missingCommission} onClick={() => update({ commission: missingCommission ? "" : "missing" })} type="button">
            {t("No commission terms")}
          </button>
        </div>
      </div>
      <LoadState empty={t("No venues match.")} error={venues.error} isEmpty={visible.length === 0} loading={venues.loading} onRetry={() => void venues.reload()}>
        <div className="ops-surface console-table-wrap">
          <table className="console-table">
            <thead>
              <tr>
                <th scope="col">{t("Venue")}</th>
                <th scope="col">{t("Status")}</th>
                <th scope="col">{t("Provider")}</th>
                <th scope="col">{t("Commission")}</th>
                <th scope="col">{t("Added")}</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((venue) => (
                <tr key={venue.id}>
                  <td>
                    <Link className="console-link" to={`/admin/venues/${venue.id}`}>{venue.name}</Link>
                    <span className="console-muted block">{venue.city}</span>
                  </td>
                  <td><StatusChip status={venue.approval_status} /></td>
                  <td><Link className="console-link" to={`/admin/providers/${venue.provider_id}`}>{venue.provider_name}</Link></td>
                  <td>
                    {venue.has_commission_rules ? (
                      <span className="ops-chip" data-tone="success">{t("Set")}</span>
                    ) : (
                      <span className="ops-chip" data-tone="warning">{t("Not set")}</span>
                    )}
                  </td>
                  <td>{dateOnly(venue.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </LoadState>
    </div>
  );
}
